<template>
  <!-- Risk management tab of processes, activities, systems and contracts (US 9370): residual risk now, how it
       has moved, where it started, and the development across the asset -->
  <div v-if="tab" class="rk-wrap">
    <!-- A process and its activities (US 9369 + 9371): the picked activity and the process's own scenarios, or the whole process -->
    <div v-if="sources && pickedKey" class="rk-showing" qid="risk-showing">
      <span class="rk-showing__lab">{{ $t('risk_tab.showing.label') }}</span>
      <div class="v2-seg" role="tablist">
        <span role="tab" :class="{ on: showing === 'picked' }" :aria-selected="showing === 'picked' ? 'true' : 'false'" @click="showing = 'picked'">
          {{ $t('risk_tab.showing.picked', { name: pickedSource.label }) }} <i>{{ countFor('picked') }}</i>
        </span>
        <span role="tab" :class="{ on: showing === 'all' }" :aria-selected="showing === 'all' ? 'true' : 'false'" @click="showing = 'all'">
          {{ $t('risk_tab.showing.all') }} <i>{{ countFor('all') }}</i>
        </span>
      </div>
    </div>
  <div class="sp-overview rk-tab" qid="risk-tab">
    <section class="v2-card sp-sec">
      <div class="sp-sec__head">
        <font-awesome-icon icon="exclamation-triangle"/>
        <h3>{{ $t('risk_tab.title') }}</h3>
        <span class="sp-grow"></span>
        <div v-if="hasPrivacy" class="v2-seg" role="tablist">
          <span v-for="key in ['business', 'privacy']"
                :key="key"
                role="tab"
                :class="{ on: lens === key }"
                :aria-selected="lens === key ? 'true' : 'false'"
                @click="lens = key">{{ $t('risk_tab.types.' + key) }}</span>
        </div>
        <b-button v-if="canAdd" size="sm" variant="success" qid="risk-add" @click="$refs.add.show()">
          <font-awesome-icon icon="plus"/> {{ $t('risk_tab.add') }}
        </b-button>
      </div>

      <div v-if="rows.length" class="rk-row rk-row--head" :class="{ 'has-applies': sources }">
        <div>{{ $t('risk_tab.scenario') }}</div>
        <div v-if="sources">{{ $t('risk_tab.applies_to') }}</div>
        <div>{{ $t('risk_tab.now') }}</div>
        <div>{{ $t('risk_tab.trend') }}</div>
        <div>{{ $t('risk_tab.change') }}</div>
        <div>{{ $t('risk_tab.start') }}</div>
      </div>
      <template v-for="row in rows">
      <div v-if="row.groupStart" :key="'g' + row.item.key" class="rk-group">
        <font-awesome-icon :icon="row.item.source.kind === 'process' ? 'sitemap' : 'cube'"/>
        <b>{{ row.item.source.kind === 'process' && showing === 'picked' ? $t('risk_tab.from_process') : row.item.source.label }}</b>
        <small v-if="row.item.source.kind === 'process'">{{ $t('risk_tab.from_process_hint') }}</small>
      </div>
      <button :key="row.item.key"
              type="button"
              class="rk-row"
              :class="{ 'is-open': openLink === row.item.key, 'has-applies': sources, 'is-inherited': sources && pickedKey && showing === 'picked' && row.item.source.kind === 'process' }"
              :qid="'risk-scenario-' + row.item.key"
              @click="open(row.item)">
        <div>
          <div class="rk-row__t">
            {{ row.item.scenario ? row.item.scenario.name : '–' }}
            <span class="rk-chip" :class="row.item.method === 'cia_risk' ? 'is-cia' : 'is-simple'">{{ $t('risk_tab.methods.' + row.item.method) }}</span>
          </div>
          <div v-if="row.item.scenario && row.item.scenario.description" class="rk-row__d">{{ row.item.scenario.description | truncate(110) }}</div>
        </div>
        <div v-if="sources">
          <span class="rk-applies" :class="row.item.source.kind === 'process' ? 'is-process' : 'is-activity'">
            <font-awesome-icon :icon="row.item.source.kind === 'process' ? 'sitemap' : 'cube'"/>{{ row.item.source.kind === 'process' ? $t('risk_tab.applies_all', { name: row.item.source.label }) : row.item.source.label }}
          </span>
        </div>
        <div>
          <RiskScore v-if="row.rating.residual"
                     :value="row.rating.residual.score"
                     :band="band(tab.scale, row.rating.residual.score)"
                     :note="toleranceNote(row.rating)"
                     :warn="above(row.rating)"></RiskScore>
          <span v-else class="rk-row__d">{{ $t('risk_tab.not_rated') }}</span>
        </div>
        <div>
          <RiskSparkline v-if="row.rating.history.length" :points="row.rating.history.map(entry => entry.score)" :max="tab.scale.max" :tolerance="row.rating.tolerance"></RiskSparkline>
        </div>
        <div><span class="rk-delta" :class="row.delta.cls">{{ row.delta.text }}</span></div>
        <div class="rk-row__d">
          <template v-if="row.rating.inherent.score">{{ $t('risk_tab.inherent_short') }} <b>{{ row.rating.inherent.score }}</b></template>
          <template v-else>–</template>
        </div>
      </button>
      </template>
      <p v-if="!rows.length" class="sx-empty">{{ $t('risk_tab.empty') }}</p>
    </section>

    <div class="sp-side">
      <section class="v2-card sp-side__card">
        <h4><font-awesome-icon icon="th-large"/>{{ $t('risk_tab.matrix_title') }}</h4>
        <small class="sp-sub">{{ $t('risk_tab.matrix_scale', { cols: tab.scale.impact.length, rows: tab.scale.probability.length }) }}</small>
        <RiskMatrix :scale="tab.scale" :counts="matrixCounts"></RiskMatrix>
        <div class="rk-legend">
          <span v-for="key in Object.keys(tab.scale.bands)" :key="key"><i :class="'is-' + key"></i>{{ $t('risk_tab.bands.' + key) }}</span>
        </div>
      </section>

      <!-- Highest residual risk of each activity, the process's own scenarios included -->
      <section v-if="sources && byActivity.length" class="v2-card sp-side__card" qid="risk-by-activity">
        <h4><font-awesome-icon icon="cube"/>{{ $t('risk_tab.by_activity') }}</h4>
        <div class="rk-bya">
          <div v-for="item in byActivity" :key="item.key" class="rk-bya__r" :class="{ on: item.key === pickedKey }">
            <b :title="item.label">{{ item.label }}</b>
            <span class="rk-bya__bar"><i :class="'is-' + band(tab.scale, item.score)" :style="{ width: (item.score ? item.score / tab.scale.max * 100 : 0) + '%' }"></i></span>
            <span class="rk-bya__v">{{ item.score || '–' }}</span>
          </div>
        </div>
        <small class="sp-sub">{{ $t('risk_tab.by_activity_hint') }}</small>
      </section>

      <section v-if="!sources && tab.development.length > 1" class="v2-card sp-side__card" qid="risk-development">
        <h4><font-awesome-icon icon="chart-line"/>{{ $t('risk_tab.development_title') }}</h4>
        <svg class="rk-dev" :viewBox="'0 0 ' + dev.w + ' ' + dev.h" role="img" :aria-label="$t('risk_tab.development_title')">
          <rect v-for="(point, index) in tab.development"
                :key="'b' + index"
                :x="dev.x(index) - 6" :y="dev.ya(point.above)" width="12" :height="dev.h - 20 - dev.ya(point.above)"
                rx="3" class="rk-dev__bar"/>
          <polyline :points="dev.line" class="rk-dev__line"/>
          <text v-for="label in devLabels" :key="'t' + label.index"
                :x="dev.x(label.index)" :y="dev.h - 4" text-anchor="middle" class="rk-dev__ax">{{ label.text }}</text>
        </svg>
        <div class="rk-legend">
          <span><i class="is-line"></i>{{ $t('risk_tab.average') }}</span>
          <span><i class="is-above"></i>{{ $t('risk_tab.above_count') }}</span>
        </div>
      </section>

      <section class="v2-card sp-side__card">
        <h4><font-awesome-icon icon="sync-alt"/>{{ $t('system_page.current.title') }}</h4>
        <div class="sp-row">
          <span>{{ $t('risk_tab.last_update') }}</span>
          <b>{{ tab.last_update ? formatDate(tab.last_update.date) + (tab.last_update.by ? ' · ' + tab.last_update.by : '') : '–' }}</b>
        </div>
        <div class="sp-row">
          <span>{{ $t('risk_tab.above_title') }}</span>
          <b :class="{ 'sx-kpi__warn': tab.above_tolerance }">{{ $tc('risk_tab.scenarios_count', tab.above_tolerance, { count: tab.above_tolerance }) }}</b>
        </div>
      </section>
    </div>

    <RiskScenarioPanel ref="panel"
                       :asset="current.asset"
                       :object="current.object"
                       :scale="tab.scale"
                       :can-edit="current.canEdit"
                       :can-delete="current.canDelete"
                       @changed="changed"
                       @hidden="openLink = null"></RiskScenarioPanel>
    <AddRiskScenarioPanel ref="add"
                          :asset="primary.asset"
                          :object="primary.object"
                          :scale="tab.scale"
                          :default-method="tab.default_method"
                          :catalogue-type="primary.catalogueType"
                          :client="tab.client"
                          :linked="tab.scenarios.filter(item => item.source.key === primary.key).map(item => item.scenario && item.scenario.id)"
                          :targets="addTargets"
                          :default-target="pickedKey || primary.key"
                          @added="changed"></AddRiskScenarioPanel>
  </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import RiskScore from '@/components/risk/RiskScore.vue';
  import RiskSparkline from '@/components/risk/RiskSparkline.vue';
  import RiskMatrix from '@/components/risk/RiskMatrix.vue';
  import RiskScenarioPanel from '@/components/risk/RiskScenarioPanel.vue';
  import AddRiskScenarioPanel from '@/components/risk/AddRiskScenarioPanel.vue';
  import {band, CIA} from '@/components/risk/riskScale.js';

  export default {
    name: 'AssetRiskTab',
    components: {
      RiskScore,
      RiskSparkline,
      RiskMatrix,
      RiskScenarioPanel,
      AddRiskScenarioPanel
    },
    props: {
      // The asset's API path: systems, processes, activities or third-parties
      asset: { type: String, required: true },
      object: { type: Object, required: true },
      // The asset type in the risk scenario catalogue: system, process, activity or third_party
      catalogueType: { type: String, required: true },
      canEdit: { type: Boolean, default: false },
      canCreate: { type: Boolean, default: false },
      canDelete: { type: Boolean, default: false },
      // A process and its activities in one tab (US 9369 + 9371), the process first:
      // [{ key, kind: 'process'|'activity', asset, object, catalogueType, label, canCreate, canEdit, canDelete }]
      sources: { type: Array, default: null },
      // The source picked on the page; with it the tab can show just that activity and the process's own scenarios
      pickedKey: { type: String, default: null }
    },
    data: function() {
      return {
        tab: null,
        lens: 'business',
        openLink: null,
        openSource: null,
        showing: 'picked'
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      // The asset itself, or the process when it shows a process and its activities
      primary() {
        if (this.sources) {
          return this.pickedSource || this.sources[0];
        }
        return { key: 'self', kind: 'self', asset: this.asset, object: this.object, catalogueType: this.catalogueType,
          canCreate: this.canCreate, canEdit: this.canEdit, canDelete: this.canDelete };
      },
      pickedSource() {
        return this.sources && this.pickedKey ? this.sources.find(source => source.key === this.pickedKey) || null : null;
      },
      // What the open scenario's panel works on
      current() {
        return this.openSource || this.primary;
      },
      canAdd() {
        return this.sources ? this.sources.some(source => source.canCreate) : this.canCreate;
      },
      addTargets() {
        if (!this.sources) {
          return null;
        }
        return this.sources.filter(source => source.canCreate).map(source => Object.assign({}, source, {
          linked: this.tab.scenarios.filter(item => item.source.key === source.key).map(item => item.scenario && item.scenario.id)
        }));
      },
      hasPrivacy() {
        return this.tab.scenarios.some(item => item.privacy_included);
      },
      // Picked: the activity's scenarios, then the process's own. All: the process's, then each activity's.
      shownScenarios() {
        let items = this.tab.scenarios;
        if (!this.sources) {
          return items;
        }
        let order = this.sources.map(source => source.key);
        if (this.pickedKey && this.showing === 'picked') {
          items = items.filter(item => item.source.kind === 'process' || item.source.key === this.pickedKey);
          order = [this.pickedKey].concat(order.filter(key => key !== this.pickedKey));
        }
        return items.slice().sort((a, b) => order.indexOf(a.source.key) - order.indexOf(b.source.key));
      },
      rows() {
        let lens = this.hasPrivacy ? this.lens : 'business';
        let last = null;
        return this.shownScenarios
          .filter(item => item.ratings[lens])
          .map(item => {
            let groupStart = !!this.sources && item.source.key !== last;
            last = item.source.key;
            return { item: item, rating: item.ratings[lens], delta: this.delta(item.ratings[lens]), groupStart: groupStart };
          });
      },
      byActivity() {
        if (!this.sources) {
          return [];
        }
        let scoreOf = item => item.ratings.business && item.ratings.business.residual ? item.ratings.business.residual.score : 0;
        let fromProcess = Math.max(0, ...this.tab.scenarios.filter(item => item.source.kind === 'process').map(scoreOf));
        return this.sources.filter(source => source.kind === 'activity').map(source => ({
          key: source.key,
          label: source.label,
          score: Math.max(fromProcess, ...this.tab.scenarios.filter(item => item.source.key === source.key).map(scoreOf)) || null
        }));
      },
      // Scenarios by where their residual risk sits; a CIA rating counts at its highest letter
      matrixCounts() {
        let counts = {};
        this.rows.forEach(function(row) {
          let entry = row.rating.residual;
          if (!entry) {
            return;
          }
          let pair = { impact: entry.impact, probability: entry.probability };
          if (entry.cia) {
            let best = CIA.filter(key => entry.parts && entry.parts[key]).sort((a, b) => entry.parts[b] - entry.parts[a])[0];
            pair = best ? entry.cia[best] : {};
          }
          let key = pair.impact + '|' + pair.probability;
          counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
      },
      // Every other month on the axis, and the last one
      devLabels() {
        let points = this.tab.development;
        return points.map((point, index) => ({ index: index, text: this.monthLabel(point.month) }))
          .filter(label => label.index % 2 === 0 || label.index === points.length - 1);
      },
      dev() {
        let points = this.tab.development;
        let w = 320, h = 130, max = this.tab.scale.max;
        let maxAbove = Math.max(1, ...points.map(point => point.above));
        let x = index => 14 + index * (w - 28) / Math.max(points.length - 1, 1);
        let ya = value => h - 20 - (value / maxAbove) * (h - 50);
        let yv = value => h - 20 - (value / max) * (h - 30);
        let line = points.filter(point => point.average !== null).map(point => x(points.indexOf(point)) + ',' + yv(point.average)).join(' ');
        return { w: w, h: h, x: x, ya: ya, line: line };
      }
    },
    watch: {
      object: function(value, old) {
        if (!old || value.id !== old.id) {
          this.load();
        }
      },
      sources: function(value, old) {
        let keys = list => (list || []).map(source => source.key + ':' + source.object.id).join(',');
        if (keys(value) !== keys(old)) {
          this.load();
        }
      }
    },
    methods: {
      band: band,
      load() {
        let self = this;
        let sources = this.sources || [this.primary];
        Promise.all(sources.map(source => piincHttp.get('risk/' + source.asset + '/' + source.object.id, { params: { client: self.getClient.slug } })
          .then(response => ({ source: source, tab: response.data }), () => null)))
          .then(function(results) {
            results = results.filter(result => result);
            if (!results.length) {
              return;
            }
            self.tab = self.merge(results);
            if (self.$refs.panel && self.openLink) {
              self.$refs.panel.refresh(self.tab.scenarios.find(item => item.key === self.openLink));
            }
          });
      },
      // One tab from the process's and its activities': scenarios carry their source, counts add up
      merge(results) {
        let first = results[0].tab;
        let updates = results.map(result => result.tab.last_update).filter(update => update && update.date).sort((a, b) => a.date < b.date ? -1 : 1);
        return Object.assign({}, first, {
          scenarios: [].concat(...results.map(result => result.tab.scenarios.map(item => Object.assign({}, item, {
            source: result.source,
            key: result.source.key + '-' + item.link_id
          })))),
          above_tolerance: results.reduce((total, result) => total + result.tab.above_tolerance, 0),
          last_update: updates.length ? updates[updates.length - 1] : null,
          development: results.length === 1 ? first.development : []
        });
      },
      countFor(mode) {
        if (!this.tab) {
          return 0;
        }
        return mode === 'picked'
          ? this.tab.scenarios.filter(item => item.source.kind === 'process' || item.source.key === this.pickedKey).length
          : this.tab.scenarios.length;
      },
      changed() {
        this.load();
        this.$emit('updated');
      },
      open(item) {
        this.openLink = item.key;
        this.openSource = item.source.kind === 'self' ? null : item.source;
        // The panel follows the scenario's process or activity
        this.$nextTick(() => this.$refs.panel.show(item, this.hasPrivacy ? this.lens : 'business'));
      },
      above(rating) {
        return !!(rating.residual && rating.tolerance && rating.residual.score > rating.tolerance);
      },
      toleranceNote(rating) {
        if (!rating.tolerance) {
          return '';
        }
        return this.above(rating) ? this.$t('risk_tab.above_tolerance') : this.$t('risk_tab.within_tolerance');
      },
      delta(rating) {
        let history = rating.history;
        if (history.length < 2) {
          return { cls: 'is-flat', text: history.length ? this.$t('risk_tab.first_rating') : '' };
        }
        let change = history[history.length - 1].score - history[history.length - 2].score;
        if (!change) {
          return { cls: 'is-flat', text: this.$t('risk_tab.no_change') };
        }
        return change < 0
          ? { cls: 'is-down', text: '▼ ' + this.$t('risk_tab.since_last', { count: -change }) }
          : { cls: 'is-up', text: '▲ ' + this.$t('risk_tab.since_last', { count: change }) };
      },
      formatDate(value) {
        return value ? moment(value).format('D MMM YYYY') : '';
      },
      monthLabel(month) {
        return moment(month + '-01').format('MMM');
      }
    },
    created() {
      this.load();
    }
  }
</script>
